import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {BusinessModel} from '../../../../core-logic/models/business/business-model';
import {ActionSheetController, Platform} from '@ionic/angular';
import {BusinessGatewayHttp} from '../../../../adapters/real/api-services/gateways/business-gateway-http';
import {Camera, CameraResultType, CameraSource} from '@capacitor/camera';
import {throwError} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {HttpEventType} from '@angular/common/http';
import {ToastService} from '../../../../common/services/toast-service';
import {ErrorModel} from '../../../../core-logic/models/shared/error-model';
import {dataURItoBlob, getFileReader} from '../../../../common/functions/utilities-functions';
import {BusinessLogoManagerService} from '../../../../common/services/business-logo-manager.service';


@Component({
  selector: 'app-loyalty-card',
  templateUrl: './loyalty-card.component.html',
  styleUrls: ['./loyalty-card.component.scss'],
})
export class LoyaltyCardComponent implements OnInit, OnChanges {
  @Input() business: BusinessModel;
  @Input() logoUri = '';
  @Input() manageLogoMode = false;
  @Input() directUpload = false;
  @Output() uploadedLogoId = new EventEmitter<string>();
  @Output() initializedLogoUri = new EventEmitter<string>();
  @Output() logoUploadPercentage = new EventEmitter<number>();
  logo: any = this.getContextualDefaultLogo();
  private readonly maxLogoSize = 1048576; // 1MB
  private readonly defaultLogo = 'assets/icons/default-business-logo.png';
  private readonly addLogo = 'assets/icons/add-business-logo.png';
  private readonly mimeTypes: string [] = ['image/jpeg', 'image/jpg', 'image/png'];

  constructor(
    private platform: Platform,
    private toastService: ToastService,
    private changeDetector: ChangeDetectorRef,
    private businessGatewayHttp: BusinessGatewayHttp,
    private actionSheetController: ActionSheetController,
    private businessLogoManagerService: BusinessLogoManagerService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.business) {
      this.business = changes.business.currentValue as BusinessModel;
      if (this.business.customizer && this.business.customizer.logo) {
        const businessStoredLogo: string = this.businessLogoManagerService
          .getLogoById(this.business.customizer.logo);
        if (businessStoredLogo) {
          this.logo = businessStoredLogo;
        } else {
          this.businessGatewayHttp.getBusinessLogoById(this.business.customizer.logo)
            .pipe(catchError(err => {
              this.logo = this.getContextualDefaultLogo();
              return throwError(err);
            }))
            .subscribe(logoBlob => {
              this.imageBlobToDataURIAndAddItToBusinessLogoManager(this.business.customizer.logo, logoBlob, this.business._id);
            });
        }

      } else {
        this.logo = this.getContextualDefaultLogo();
      }
    }

    if (changes.logoUri) { this.logo = this.getContextualDefaultLogo(); }
  }

  ngOnInit() {}

  async presentLogoActionSheet() {
    if (this.manageLogoMode) {
      const buttons = [{
        text: 'Importer le logo',
        handler: () => this.getLogoFromPhotoLibrary(),
      },
        {
          text: 'Annuler',
          role: 'cancel',
          handler: () => {}
        }];

      if (this.directUpload && this.business.customizer && this.business.customizer.logo) {
        this.addRemoteDeleteButtonToActionSheet(buttons);
      }

      if (!this.directUpload && this.logo !== this.addLogo) {
        this.addDeleteButtonToActionSheet(buttons);
      }

      const logoActionSheet = await this.actionSheetController.create({
        header: 'Gestionnaire du logo',
        buttons
      });
      await logoActionSheet.present();
    }
  }

  async getLogoFromPhotoLibrary() {
    const cameraPhoto = await Camera.getPhoto({
      quality: 100,
      allowEditing: false,
      source: CameraSource.Photos,
      resultType: CameraResultType.DataUrl,
    });
    const imageDataURI = cameraPhoto.dataUrl;
    const imageBlob = dataURItoBlob(imageDataURI);
    if (imageBlob.size > this.maxLogoSize) {
      await this.toastService.presentErrorMessage('Le logo ne doit pas dépasser 300KB');
    } else if (!this.mimeTypes.includes(imageBlob.type)) {
      await this.toastService.presentErrorMessage('Le logo doit être au format JPEG ou JPG ou PNG');
    } else {
      this.logo = imageDataURI;
      this.initializedLogoUri.emit(this.logo);
      if (this.directUpload) {
        this.uploadBusinessLogo(imageBlob);
      }
    }
  }

  private uploadBusinessLogo(logoBlob: Blob) {
    this.businessGatewayHttp.uploadBusinessLogo(logoBlob)
      .pipe(catchError(err => {
        this.toastService.presentErrorMessage((err.error as ErrorModel).message);
        if (this.business.customizer && this.business.customizer.logo) {
          const businessStoredLogo: string = this.businessLogoManagerService.getLogoById(this.business.customizer.logo);
          if (businessStoredLogo) {
            this.logo = businessStoredLogo;
          } else {
            this.logo = this.getContextualDefaultLogo();
          }
        } else {
          this.logo = this.getContextualDefaultLogo();
        }
        return throwError(err);
      })).subscribe(eventType => {
      switch (eventType.type) {
        case HttpEventType.Sent:
          break;
        case HttpEventType.UploadProgress:
          const percentDone = Math.round( eventType.loaded * 100 / eventType.total) / 100;
          this.logoUploadPercentage.emit(percentDone);
          break;
        case HttpEventType.Response:
          if (this.business._id && this.business.customizer && this.business.customizer.logo) {
            this.businessGatewayHttp.deleteBusinessLogo(this.business.customizer.logo).pipe(take(1)).subscribe(isItDeleted => {
              if (isItDeleted) {this.businessLogoManagerService.removeLogoById(this.business.customizer.logo); }
            });
          }
          const uploadedLogoId: string = eventType.body.id;
          this.imageBlobToDataURIAndAddItToBusinessLogoManager(uploadedLogoId, logoBlob,
            this.business._id ? this.business._id : null);
          this.uploadedLogoId.emit(uploadedLogoId);
      }
    });
  }

  private imageBlobToDataURIAndAddItToBusinessLogoManager(logoId: string, imageBlob: Blob, businessId?: string) {
    const reader = getFileReader();
    reader.onloadend = () => {
      this.logo = reader.result as string;
      this.businessLogoManagerService.addBusinessLogo({logo_id: logoId, data: this.logo, business_id: businessId});
      this.changeDetector.detectChanges();
    };
    reader.readAsDataURL(imageBlob);
  }

  private addRemoteDeleteButtonToActionSheet(buttons: ({ handler: () => Promise<void>; text: string } |
    { handler: () => void; role: string; text: string })[]) {
    buttons.push({
      text: 'Supprimer le logo',
      role: '',
      handler: () => {
        this.businessGatewayHttp.deleteBusinessLogo(this.business.customizer.logo).subscribe(isItDeleted => {
          if (isItDeleted) {
            this.businessLogoManagerService.removeLogoById(this.business.customizer.logo);
            this.logo = this.getContextualDefaultLogo();
            this.uploadedLogoId.emit('');
          }
        });
      },
    });
  }

  private addDeleteButtonToActionSheet(buttons: ({ handler: () => Promise<void>; text: string } |
    { handler: () => void; role: string; text: string })[]) {
    buttons.push({
      text: 'Supprimer le logo',
      role: '',
      handler: () => {
        this.logo = this.addLogo;
        this.initializedLogoUri.emit('');
      },
    });
  }

  private getContextualDefaultLogo() {
    if (this.manageLogoMode) {
      if (this.logoUri) {
        return this.logoUri;
      }
      return this.addLogo;

    } else {
       return this.defaultLogo;
    }
  }
}


